<template>
  <TransitionRoot as="template" :show="show">
    <Dialog as="div" class="fixed inset-0 overflow-hidden z-50" @close="emitClose">
      <div class="absolute inset-0 overflow-hidden">
        <DialogOverlay class="absolute inset-0 bg-black opacity-40" />

        <div class="pointer-events-none fixed inset-y-0 right-0 flex max-w-full pl-4 sm:pl-8">
          <TransitionChild
            as="template"
            enter="transform transition ease-in-out duration-500 sm:duration-700"
            enter-from="translate-x-full"
            enter-to="translate-x-0"
            leave="transform transition ease-in-out duration-400 sm:duration-500"
            leave-from="translate-x-0"
            leave-to="translate-x-full"
          >
            <div class="pointer-events-auto w-screen max-w-md">
              <form class="flex h-full flex-col modalShell">
                <!-- HEADER -->
                <div class="shrink-0 modalHeader">
                  <div class="flex items-start justify-between gap-4">
                    <div class="min-w-0">
                      <DialogTitle class="text-lg font-semibold tracking-tight text-white">
                        Dodaj region dostawy
                      </DialogTitle>
                      <p class="mt-1 text-sm text-slate-300">
                        Utwórz nowy region tras do przypisywania zamówień i obszarów dostaw.
                      </p>
                    </div>

                    <button
                      type="button"
                      @click.prevent="emitClose"
                      class="iconBtn shrink-0"
                      aria-label="Zamknij panel"
                    >
                      <svg class="h-4 w-4 text-slate-200" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                        <path
                          fill-rule="evenodd"
                          d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                          clip-rule="evenodd"
                        />
                      </svg>
                    </button>
                  </div>
                </div>

                <!-- BODY -->
                <div class="min-h-0 flex-1 overflow-y-auto">
                  <div class="space-y-4 p-4 sm:p-5">
                    <section class="sectionCard">
                      <div class="sectionHead">
                        <div>
                          <div class="sectionTitle">Dane regionu</div>
                          <div class="sectionSub">
                            Uzupełnij nazwę i opis, które będą widoczne przy przypisywaniu regionów do tras i zamówień.
                          </div>
                        </div>
                      </div>

                      <div class="sectionBody space-y-4">
                        <div class="fieldWrap">
                          <label class="fieldLabel">Nazwa regionu*</label>
                          <input
                            type="text"
                            v-model="form.name"
                            maxlength="60"
                            :class="inputCls(errors.name)"
                            placeholder="np. wielkopolska-południe"
                          />
                          <p v-if="errors.name" class="fieldError">
                            Podaj nazwę regionu.
                          </p>
                        </div>

                        <div class="fieldWrap">
                          <label class="fieldLabel">Opis regionu*</label>
                          <textarea
                            v-model="form.description"
                            maxlength="100"
                            :class="textareaCls(errors.description)"
                            rows="4"
                            placeholder="np. Okolice Kalisza, Ostrzeszowa i południowej części województwa."
                          ></textarea>
                          <p v-if="errors.description" class="fieldError">
                            Podaj opis regionu.
                          </p>
                        </div>
                      </div>
                    </section>
                  </div>
                </div>

                <!-- FOOTER -->
                <div class="shrink-0 border-t border-slate-700 bg-slate-800 px-4 py-4">
                  <div class="flex items-center justify-between gap-3">
                    <div class="text-xs text-slate-400">
                      <span v-if="loading" class="text-slate-200">Zapisywanie danych...</span>
                      <span v-else>Sprawdź nazwę i opis regionu przed zapisaniem.</span>
                    </div>

                    <div class="flex items-center gap-2">
                      <button
                        v-if="!loading"
                        @click.prevent="emitClose"
                        type="button"
                        class="btnSecondary"
                      >
                        Zamknij
                      </button>

                      <button
                        v-if="!loading"
                        @click.prevent="validateAndSave"
                        type="button"
                        class="btnPrimary whitespace-nowrap"
                      >
                        Dodaj region
                      </button>
                    </div>
                  </div>
                </div>
              </form>
            </div>
          </TransitionChild>
        </div>
      </div>
    </Dialog>
  </TransitionRoot>
</template>

<script>
import {
  Dialog,
  DialogOverlay,
  DialogTitle,
  TransitionChild,
  TransitionRoot
} from '@headlessui/vue'

import { db } from '@/firebase/gfbconf.js'

export default {
  name: 'ModalNewRegion',
  components: {
    Dialog,
    DialogOverlay,
    DialogTitle,
    TransitionChild,
    TransitionRoot
  },
  props: {
    show: { type: Boolean, default: false }
  },
  data() {
    return {
      loading: false,
      form: {
        name: '',
        description: ''
      },
      errors: {
        name: false,
        description: false
      }
    }
  },
  methods: {
    inputCls(hasError) {
      return [
        'inputField',
        hasError ? 'inputFieldError' : ''
      ]
    },

    textareaCls(hasError) {
      return [
        'inputField',
        'textareaField',
        hasError ? 'inputFieldError' : ''
      ]
    },

    resetErrors() {
      this.errors.name = false
      this.errors.description = false
    },

    validate() {
      this.resetErrors()
      let errs = 0

      if (!this.form.name.trim()) {
        this.errors.name = true
        errs++
      }

      if (!this.form.description.trim()) {
        this.errors.description = true
        errs++
      }

      return errs === 0
    },

    generateLocalId() {
      return (
        Date.now().toString(36) +
        Math.random().toString(36).slice(2)
      )
    },

    buildMeta() {
      const now = new Date()

      const unix = Math.floor(now.getTime() / 1000)
      const year = now.getFullYear()
      const monthNum = now.getMonth() + 1
      const dayNum = now.getDate()

      const pad = (n) => n.toString().padStart(2, '0')
      const asText = `${pad(dayNum)}.${pad(monthNum)}.${year}`

      const hours = now.getHours()
      const minutes = now.getMinutes()
      const tz = now.getTimezoneOffset()
      const sign = tz <= 0 ? '+' : '-'
      const abs = Math.abs(tz)
      const offH = pad(Math.floor(abs / 60))
      const offM = pad(abs % 60)

      const dateLocal = `${asText}T${pad(hours)}:${pad(minutes)}:00${sign}${offH}:${offM}`

      const ud = this.$store.state.userData || {}
      const creatorId = ud.id || null
      const creatorName = `${ud.name || ''} ${ud.surname || ''}`.trim() || 'Nieznany użytkownik'

      return {
        createdDate: {
          asText,
          date: dateLocal,
          dayNum,
          monthNum,
          unix,
          year
        },
        creator: {
          id: creatorId,
          name: creatorName
        }
      }
    },

    async validateAndSave() {
      if (!this.validate()) return

      this.loading = true

      try {
        const regionsDoc = await db.collection('routeRegions').doc('regions').get()

        let regions = []
        if (regionsDoc.exists && Array.isArray(regionsDoc.data().regions)) {
          regions = regionsDoc.data().regions
        }

        const newRegion = {
          id: this.generateLocalId(),
          name: this.form.name.trim(),
          description: this.form.description.trim(),
          meta: this.buildMeta()
        }

        regions.push(newRegion)

        await db.collection('routeRegions').doc('regions').set(
          { regions },
          { merge: true }
        )

        this.$store.commit('setNotification', {
          show: true,
          head: 'Region dodany!',
          subheader: 'Nowy region tras został zapisany.',
          success: true
        })

        this.emitClose()
        this.$emit('reloadRegions')
      } catch (e) {
        console.error(e)
        this.$store.commit('setNotification', {
          show: true,
          head: 'Błąd zapisu regionu',
          subheader: 'Nie udało się zapisać nowego regionu.',
          success: false
        })
      } finally {
        this.loading = false
      }
    },

    emitClose() {
      this.$emit('closeNewRegionModal')
      this.form.name = ''
      this.form.description = ''
      this.resetErrors()
      this.loading = false
    }
  }
}
</script>

<style lang="scss" scoped>
.modalShell {
  height: 100%;
  background: rgba(30, 41, 59, 0.98);
  border-left: 1px solid rgba(71, 85, 105, 0.8);
  box-shadow: -20px 0 60px rgba(0, 0, 0, 0.35);
}

.modalHeader {
  padding: 20px 20px 18px 20px;
  border-bottom: 1px solid rgba(51, 65, 85, 1);
  background: linear-gradient(135deg, rgba(15, 23, 42, 0.95), rgba(30, 41, 59, 0.92));
}

.sectionCard {
  overflow: hidden;
  border: 1px solid rgba(71, 85, 105, 0.85);
  border-radius: 22px;
  background: rgba(30, 41, 59, 0.72);
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.18);
  position: relative;
}

.sectionHead {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  padding: 16px 16px 14px 16px;
  border-bottom: 1px solid rgba(51, 65, 85, 0.9);
  background: rgba(51, 65, 85, 0.32);
}

.sectionTitle {
  font-size: 14px;
  font-weight: 700;
  color: #ffffff;
}

.sectionSub {
  margin-top: 4px;
  font-size: 11px;
  line-height: 1.5;
  color: rgba(148, 163, 184, 1);
}

.sectionBody {
  padding: 16px;
}

.fieldWrap {
  display: flex;
  flex-direction: column;
}

.fieldLabel {
  display: block;
  margin-bottom: 6px;
  font-size: 12px;
  font-weight: 600;
  color: #e2e8f0;
}

.fieldError {
  margin-top: 6px;
  font-size: 12px;
  color: #f87171;
}

.inputField {
  display: block;
  width: 100%;
  border-radius: 14px;
  border: 1px solid rgba(100, 116, 139, 0.75);
  background: rgba(15, 23, 42, 0.7);
  color: #f8fafc;
  padding: 11px 14px;
  font-size: 14px;
  line-height: 1.3;
  transition: all 0.18s ease;
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.03);
}

.inputField::placeholder {
  color: rgba(148, 163, 184, 0.75);
}

.inputField:focus {
  outline: none;
  border-color: rgba(99, 102, 241, 0.85);
  box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.15);
}

.textareaField {
  resize: vertical;
  min-height: 96px;
}

.inputFieldError {
  border-color: rgba(239, 68, 68, 0.9);
}

.iconBtn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 38px;
  width: 38px;
  border-radius: 14px;
  border: 1px solid rgba(100, 116, 139, 0.75);
  background: rgba(51, 65, 85, 0.8);
  transition: all 0.18s ease;
}

.iconBtn:hover {
  background: rgba(71, 85, 105, 0.95);
}

.btnSecondary,
.btnPrimary {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 40px;
  padding: 0 16px;
  border-radius: 14px;
  font-size: 12px;
  font-weight: 700;
  transition: all 0.18s ease;
}

.btnSecondary {
  border: 1px solid rgba(100, 116, 139, 0.8);
  background: rgba(51, 65, 85, 0.75);
  color: #f1f5f9;
}

.btnSecondary:hover {
  background: rgba(71, 85, 105, 0.95);
}

.btnPrimary {
  border: 1px solid rgba(59, 130, 246, 0.55);
  background: rgba(37, 99, 235, 0.22);
  color: #dbeafe;
}

.btnPrimary:hover {
  background: rgba(37, 99, 235, 0.3);
}

@media (min-width: 640px) {
  .modalHeader {
    padding: 24px 24px 20px 24px;
  }

  .sectionBody,
  .sectionHead {
    padding-left: 18px;
    padding-right: 18px;
  }
}
</style>